import type { ConfirmDialogProps as PrimeConfirmDialogProps } from "primereact/confirmdialog";
import type { ReactNode } from "react";

import { withDefaults } from "../../utils/with-defaults";
import { DIALOG_DEFAULTS } from "../dialog";
import { primeIcon } from "../icon";
import type { ConfirmDialogProps } from "./types-confirm-dialog";
import { CONFIRM_ACCEPT_LABEL, CONFIRM_REJECT_LABEL } from "./messages";

/** Ícone da variante destrutiva: triângulo de alerta de 32 (frame `confirmdialog`). */
const ALERT_ICON = primeIcon("exclamation-triangle", 32);

/**
 * `accept` do Prime com a chamada da tela guardada em `run`: o rodapé espera `run()` (que pode ser assíncrono) e só então
 * aciona o `accept` do Prime com `skip` ligado, para fechar sem chamar a tela de novo. Sem rodapé do DS, o Prime chama
 * a tela direto (o `accept` assíncrono não é aguardado).
 */
export interface GatedAccept {
  (): void | Promise<void>;
  run: (() => void | Promise<void>) | undefined;
  skip: boolean;
}

const gateAccept = (accept: ConfirmDialogProps["accept"]): GatedAccept => {
  const gated: GatedAccept = Object.assign(() => (gated.skip ? undefined : accept?.()), {
    run: accept,
    skip: false,
  });
  return gated;
};

/** Ícone em função (os do DS) com o `data-testid` `<testId>-icon`; ícone de classe ou nó fica como veio. */
const withIconTestId = (icon: ConfirmDialogProps["icon"], testId: string | undefined) => {
  if (icon === undefined) return null;
  if (typeof icon !== "function" || !testId) return { icon };
  const render = icon as (options: { iconProps?: object }) => ReactNode;
  return {
    icon: (options: { iconProps?: object }) =>
      render({ ...options, iconProps: { ...options.iconProps, "data-testid": `${testId}-icon` } }),
  };
};

/** Atributo que leva o `testId` ao rodapé (o Prime só entrega ao rodapé as props que mescla; extras camelCase vazariam no DOM). */
export const TEST_ID_PREFIX_ATTRIBUTE = "data-confirm-testid";

/**
 * Aplica os padrões do DS às props do Prime, para o `<ConfirmDialog>` e o `confirmDialog({...})` (o Prime mescla as props
 * do componente com as da chamada, então as duas entradas passam por aqui). O Prime não conhece os extras: viram
 * `data-testid`, `data-confirm-testid`, `data-variant`, `icon` e `accept`.
 */
export function withConfirmDefaults(props: ConfirmDialogProps): PrimeConfirmDialogProps {
  const { variant = "neutral", testId, size = "sm", accept, icon, ...rest } = props;

  return {
    ...withDefaults(
      {
        ...DIALOG_DEFAULTS,
        acceptLabel: CONFIRM_ACCEPT_LABEL,
        // O foco inicial segue o do Dialog (no X), como nos diálogos atuais; `defaultFocus="accept"` é o do Prime.
        defaultFocus: "none",
        rejectLabel: CONFIRM_REJECT_LABEL,
        "data-variant": variant,
        "data-size": size,
        ...(testId
          ? { "data-testid": `${testId}-dialog`, [TEST_ID_PREFIX_ATTRIBUTE]: testId }
          : null),
      },
      rest,
    ),
    ...withIconTestId(icon ?? (variant === "destructive" ? ALERT_ICON : undefined), testId),
    accept: gateAccept(accept),
  } as PrimeConfirmDialogProps;
}
